import React, { useState } from 'react';
import { IconPhone, IconX } from '@tabler/icons-react';

const getCardColors = (cardType) => {
  switch (cardType) {
    case 'technologies': 
      return { primary: '', hover: '', customBg: 'rgb(255, 156, 35)', customHover: 'rgb(235, 136, 15)' };
    case 'farmstays':
      return { primary: 'bg-green-500', hover: 'hover:bg-green-600' };
    case 'constructions': 
      return { primary: '', hover: '', customBg: 'rgb(198, 171, 63)', customHover: 'rgb(178, 151, 43)' };
    case 'home': 
      return { primary: '', hover: '', customBg: 'rgb(6 44 107)', customHover: 'rgb(6 44 107)' };
    default: 
      return { primary: '', hover: '', customBg: 'rgb(255, 156, 35)', customHover: 'rgb(235, 136, 15)' };
  }
};

const getEmailByCardType = (cardType) => {
  switch (cardType) {
    case 'technologies': return 'tech@maifloer.com';
    case 'farmstays': return 'maifloerfarmstays@gmail.com';
    case 'constructions': return 'construction@maifloer.com';
    case 'home': return 'teammaifloer@gmail.com';
    default: return 'teammaifloer@gmail.com';
  }
};

const EnquiryButton = ({ cardData = {} }) => {
  const { cardType } = cardData;
  const colors = getCardColors(cardType);
  const toEmail = getEmailByCardType(cardType);

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', mobile: '', message: '' });
  const [error, setError] = useState('');

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return 'Please enter your name.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return 'Please enter a valid email.';
    if (!/^[0-9+\-\s()]{7,15}$/.test(form.mobile.trim())) return 'Please enter a valid mobile number.';
    if (!form.message.trim()) return 'Please enter your message.';
    return '';
  };

  const buildMailto = () => {
    const subject = `New Enquiry${cardType ? ` – ${cardType}` : ''} from ${form.name}`;
    const bodyLines = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      `Mobile: ${form.mobile}`,
      `Service: ${cardType || 'General'}`,
      '',
      'Message:',
      form.message
    ];
    const body = bodyLines.join('\n');
    return `mailto:${toEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const v = validate();
    if (v) return setError(v);

    window.location.href = buildMailto();
    setTimeout(() => {
      setOpen(false);
      setForm({ name: '', email: '', mobile: '', message: '' });
      setError('');
    }, 300);
  };

  const close = () => {
    setOpen(false);
    setError('');
  };

  return (
    <>
      {/* Floating Enquiry Button */}
      <button
        onClick={() => {
          const pageHeight = document.documentElement.scrollHeight;
          const viewportHeight = window.innerHeight;
          const centerPosition = (pageHeight - viewportHeight) / 2;
          window.scrollTo({ top: centerPosition, behavior: 'smooth' });
          setTimeout(() => setOpen(true), 300);
        }}
        className={`fixed bottom-6 right-6 md:bottom-10 md:right-10 w-20 h-20 md:w-28 md:h-28 
          ${colors.primary} ${colors.hover} text-white rounded-full shadow-xl flex flex-col 
          justify-center items-center text-center z-50`}
        style={{
          backgroundColor: colors.customBg || undefined,
          userSelect: "none",
          WebkitUserSelect: "none",
          msUserSelect: "none"
        }}
        onMouseEnter={(e) => colors.customHover && (e.target.style.backgroundColor = colors.customHover)}
        onMouseLeave={(e) => colors.customBg && (e.target.style.backgroundColor = colors.customBg)}
        aria-label="Open enquiry form"
      >
        <IconPhone size={20} className="mb-1 md:mb-2 md:size-7" style={{ userSelect: "none" }} />

        <span
          className="text-[10px] leading-tight md:text-sm text-center"
          style={{
            userSelect: "none",
            WebkitUserSelect: "none",
            msUserSelect: "none"
          }}
        >
          ENQUIRY<br />BUTTON
        </span>
      </button>

      {/* Modal */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
          {/* Backdrop */}
          <button className="absolute inset-0 bg-black/50" onClick={close} aria-label="Close enquiry form" />

          {/* Dialog */}
          <div className="relative z-10 w-[92%] max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl">
                Enquiry {cardType ? `– ${cardType[0].toUpperCase()}${cardType.slice(1)}` : ''}
              </h2>
              <button onClick={close} className="p-1 rounded-full hover:bg-black/5" aria-label="Close">
                <IconX />
              </button>
            </div>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">Name</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-800"
                  placeholder="Your name"
                  required
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-800"
                  placeholder="you@example.com"
                  required
                />
              </div>

              <div>
                <label htmlFor="mobile" className="block text-sm font-medium mb-1">Mobile Number</label>
                <input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  value={form.mobile}
                  onChange={onChange}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-800"
                  placeholder="+91 98765 43210"
                  required
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium mb-1">Message</label>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={form.message}
                  onChange={onChange}
                  className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-800"
                  placeholder="How can we help?"
                  required
                />
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}

              <button
                type="submit"
                className={`w-full ${colors.primary} ${colors.hover} text-white rounded-xl px-4 py-2 font-medium`}
                style={{ backgroundColor: colors.customBg || undefined }}
              >
                Send
              </button>
            </form>

            <p className="mt-4 text-xs text-gray-500 text-center">
              This will open your mail app with the details prefilled. You can review and send from there.
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default EnquiryButton;
